// src/component/MMInfoFeedList.js
import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

import { db, auth } from "../api/firebase";
import {
  collection,
  query,
  onSnapshot,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";

import { AppThemeContext } from "./AppShell";
import Post from "./PreviewPost";

const ALLOWED_SEKAIMURA_KEY = "sekaiMura";

// ------------------------------------
// ユーティリティ
// ------------------------------------
const truncate = (text, max = 120) =>
  text && text.length > max ? text.slice(0, max) + "…" : text || "";

const formatDate = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : ts;
  return d.toLocaleDateString("ja-JP");
};

// YouTube ID 抽出（m.youtube.com も許容）
const extractYouTubeId = (url) => {
  if (!url || typeof url !== "string") return null;

  // m.youtube.com → youtube.com に統一
  url = url.replace("m.youtube.com", "youtube.com");

  const regex =
    /(?:youtube\.com\/(?:.*v=|v\/|e\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

const getYouTubeThumbnailFromUrl = (url) => {
  const id = extractYouTubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// Post から画像サムネを拾う
const getImageThumbnailFromPost = (post) => {
  const media = post?.media;
  if (!media || media.type !== "image" || !media.src) return null;

  let src = media.src;

  if (Array.isArray(src)) {
    const first = src[0];
    if (typeof first === "string") {
      src = first;
    } else if (Array.isArray(first?.url) && first.url[0]) {
      src = first.url[0];
    } else if (typeof first?.url === "string") {
      src = first.url;
    }
  }

  if (typeof src === "string" && src) return src;
  return null;
};

// Post から動画サムネ（YouTube のみ）を拾う
const getVideoThumbnailFromPost = (post) => {
  const media = post?.media;
  if (!media || media.type !== "video" || !media.src) return null;

  let src = media.src;

  if (Array.isArray(src)) {
    const first = src[0];
    if (typeof first === "string") {
      src = first;
    } else if (typeof first?.url === "string") {
      src = first.url;
    }
  }

  if (typeof src !== "string") return null;

  // YouTube のときだけ静止画を取得
  return getYouTubeThumbnailFromUrl(src);
};

export default function MMInfoFeedList() {
  const theme = useContext(AppThemeContext) || {};
  const surface = theme.surfaceBg || "#FFFFFF";
  const railText = theme.railText || "rgba(40,40,60,0.9)";
  const accent = theme.railActive || "#5A35FF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";

  const [posts, setPosts] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [userPayments, setUserPayments] = useState(null);
  const [isSekaimura, setIsSekaimura] = useState(false);
  const [userTerm] = useState(null);

  const [selectedPost, setSelectedPost] = useState(null);

  const loadMoreRef = useRef(null);

  // ==== ユーザー情報取得（payments / isSekaimura） ====
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setUserPayments([]);
          setIsSekaimura(false);
          return;
        }

        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (!snap.exists()) {
          setUserPayments([]);
          setIsSekaimura(false);
          return;
        }

        const data = snap.data();
        const payments = Array.isArray(data.payments) ? data.payments : [];
        const phases = payments
          .map((p) => String(p?.paymentPhase ?? "").trim())
          .filter((v) => v);

        setUserPayments(phases);
        setIsSekaimura(Boolean(data.isSekaimura));
      } catch (e) {
        console.error("Error fetching user info for feeds:", e);
        setUserPayments([]);
        setIsSekaimura(false);
      }
    };

    fetchUserInfo();
  }, []);

  // ==== 最初のページ（リアルタイム購読） ====
  useEffect(() => {
    if (userPayments === null) return;

    const q = query(
      collection(db, "posts"),
      where("isVisible", "==", true),
      orderBy("timestamp", "desc"),
      limit(6)
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs;

      const filtered = docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((post) => {
          const allowed = Array.isArray(post.allowedTerm)
            ? post.allowedTerm.map((t) => String(t).trim())
            : [];
          return allowed.some(
            (term) =>
              userPayments.includes(term) ||
              (isSekaimura && term === ALLOWED_SEKAIMURA_KEY)
          );
        });

      setPosts(filtered);
      setLastVisible(docs[docs.length - 1] || null);
      setHasMore(docs.length >= 6);
    });

    return () => unsub();
  }, [userPayments, isSekaimura]);

  // ==== 追加読み込み ====
  const loadMorePosts = async () => {
    if (!lastVisible || loading || !hasMore) return;
    setLoading(true);

    const q = query(
      collection(db, "posts"),
      where("isVisible", "==", true),
      orderBy("timestamp", "desc"),
      startAfter(lastVisible),
      limit(6)
    );

    try {
      const snap = await getDocs(q);
      const docs = snap.docs;

      const newPosts = docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((post) => {
          const allowed = Array.isArray(post.allowedTerm)
            ? post.allowedTerm.map((t) => String(t).trim())
            : [];
          return allowed.some(
            (term) =>
              userPayments?.includes(term) ||
              (isSekaimura && term === ALLOWED_SEKAIMURA_KEY)
          );
        });

      setPosts((prev) => [...prev, ...newPosts]);
      setLastVisible(docs[docs.length - 1] || null);
      setHasMore(docs.length >= 6);
    } catch (e) {
      console.error("Error loading more posts:", e);
    } finally {
      setLoading(false);
    }
  };

  // ==== 無限スクロール監視 ====
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore) {
          loadMorePosts();
        }
      },
      { root: null, rootMargin: "0px", threshold: 0.9 }
    );

    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [lastVisible, loading, hasMore]);

  // ==== サムネイル UI（画像 / 動画ありのときだけ表示） ====
// ==== サムネイル UI（画像 / 動画が「本当にある」時だけ表示） ====
const renderThumb = (post) => {
    const dateLabel = post.timestamp ? formatDate(post.timestamp) : "日付未設定";
    const media = post.media;
  
    // 画像サムネイル（有効な URL のときだけ）
    const imageThumb = getImageThumbnailFromPost(post);
  
    // 動画サムネイル（YouTube のサムネが取れたときだけ）
    const videoThumb = getVideoThumbnailFromPost(post);
  
    // 画像も YouTube サムネも取れなければ、サムネイル自体を表示しない
    const hasThumb = Boolean(imageThumb || videoThumb);
    if (!hasThumb) return null;
  
    // 「サムネが動画由来かどうか」だけを見る
    const isVideo = Boolean(videoThumb);
    const bgImage = imageThumb || videoThumb;
  
    return (
      <Box
        sx={{
          flexShrink: 0,
          width: { xs: 96, sm: 120 },
          height: { xs: 72, sm: 80 },
          borderRadius: 3,
          boxShadow:
            "0 10px 25px rgba(0,0,0,0.10), inset 0 0 0 1px rgba(255,255,255,0.9)",
          position: "relative",
          overflow: "hidden",
          background: "transparent",
        }}
      >
        {/* 画像 or YouTube サムネ */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
  
        {/* 動画の場合のみ、再生アイコンを重ねる（再生はしない） */}
        {isVideo && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "50%",
                bgcolor: accent,
                boxShadow:
                  "0 10px 24px rgba(0,0,0,0.25), 0 0 0 4px rgba(255,255,255,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PlayArrowRoundedIcon sx={{ fontSize: 28, color: "#fff" }} />
            </Box>
          </Box>
        )}
  
        {/* 日付バッジ */}
        <Box
          sx={{
            position: "absolute",
            bottom: 6,
            left: 0,
            right: 0,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.2,
              borderRadius: 999,
              bgcolor: "rgba(255,255,255,0.88)",
              color: railText,
              fontSize: 11,
            }}
          >
            {dateLabel}
          </Typography>
        </Box>
      </Box>
    );
  };
  

  // ------------------------------------
  // レンダリング
  // ------------------------------------
  return (
    <Box>
      {/* 一覧部分 */}
      <Stack spacing={1.5} sx={{ width: "100%", maxWidth: "100%" }}>
        {posts.map((post) => {
          const teaser =
            truncate(
              post.summary ||
                post.body ||
                post.text ||
                post.content ||
                post.message ||
                ""
            ) || "本文がまだ登録されていません。";

          const dateLabel = post.timestamp
            ? formatDate(post.timestamp)
            : "日付未設定";

          return (
            <Card
              key={post.id}
              onClick={() => setSelectedPost(post)}
              sx={{
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.95)",
                border: `1px solid ${border}`,
                boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "0 14px 30px rgba(0,0,0,0.10)",
                  transform: "translateY(-1px)",
                },
                transition: "all .18s ease-out",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: { xs: 1.5, sm: 2 },
                  width: "100%",
                  maxWidth: "100%",
                  p: 1.5,
                }}
              >
                {/* サムネイル */}
                {renderThumb(post)}

                {/* 右側：日付 + 本文 */}
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: "100%",
                    maxWidth: "100%",
                  }}
                >
                  {/* 上段：日付（タイトル代わり） */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: railText,
                      mb: 0.4,
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateLabel}
                  </Typography>

                  {/* 本文（2行まで） */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 12,
                      color: "rgba(0,0,0,0.65)",
                      lineHeight: 1.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      wordBreak: "break-word",
                    }}
                  >
                    {teaser}
                  </Typography>
                </Box>
              </Box>
            </Card>
          );
        })}
      </Stack>

      {/* ローディング / 「もっと読み込む」 */}
      <Box sx={{ mt: 2, textAlign: "center" }}>
        {loading && (
          <Stack direction="row" spacing={1} justifyContent="center">
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: railText }}>
              読み込み中…
            </Typography>
          </Stack>
        )}

        {!loading && posts.length === 0 && !hasMore && (
          <Typography
            variant="body2"
            sx={{ color: "rgba(0,0,0,0.6)", mt: 1.5 }}
          >
            表示できるお知らせがありません。
          </Typography>
        )}

        {hasMore && (
          <Box ref={loadMoreRef} sx={{ py: 1 }}>
            {!loading && (
              <Button size="small" onClick={loadMorePosts}>
                もっと読み込む
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* ▼ お知らせ内容ダイアログ */}
      <Dialog
        open={Boolean(selectedPost)}
        onClose={() => setSelectedPost(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: surface,
          },
        }}
      >
        {selectedPost && (
          <>
            <DialogTitle
              sx={{
                pb: 1,
                fontWeight: 700,
                color: railText,
              }}
            >
              {selectedPost.timestamp
                ? formatDate(selectedPost.timestamp)
                : "基本情報"}
            </DialogTitle>
            <Divider />
            <DialogContent
              sx={{
                pt: 2,
                pb: 3,
                bgcolor: "rgba(255,255,255,0.9)",
              }}
            >
              <Post post={selectedPost} currentUser={{ term: userTerm }} />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
